import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    verifyToken(token);
  }, [searchParams]);

  const verifyToken = async (token) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/confirm-email-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await res.json();

      if (res.ok) {
        setStatus('success');
        setMessage(data.message);
        setNewEmail(data.new_email);
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed. The link may have expired.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Failed to verify email. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="verify-email-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex flex-col items-center gap-4">
            {status === 'verifying' && (
              <>
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                <span>Verifying Email...</span>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle className="w-12 h-12 text-green-600" />
                <span>Email Verified!</span>
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle className="w-12 h-12 text-red-600" />
                <span>Verification Failed</span>
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-slate-600">{message}</p>

          {status === 'success' && newEmail && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-center gap-2 text-green-800">
                <Mail className="w-5 h-5" />
                <span className="font-medium">{newEmail}</span>
              </div>
              <p className="text-sm text-green-700 mt-2">
                Please use this email to log in from now on.
              </p>
            </div>
          )}

          <div className="pt-4">
            {status === 'success' ? (
              <Button onClick={() => navigate('/login')} className="w-full" data-testid="go-to-login-btn">
                Go to Login
              </Button>
            ) : status === 'error' ? (
              <div className="space-y-3">
                <Button variant="outline" onClick={() => navigate('/profile')} className="w-full">
                  Back to Profile
                </Button>
                <p className="text-sm text-slate-500">
                  Already logged in?{' '}
                  <Link to="/profile" className="text-blue-600 hover:underline">
                    Request a new verification email
                  </Link>
                </p>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
